import { useState, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Upload,
  FileSpreadsheet,
  Download,
  AlertCircle,
  CheckCircle2,
  X,
  Loader2,
} from "lucide-react";

import { API_BASE_URL as API } from "../lib/backendUrl";

const CSVImportDialog = ({ open, onOpenChange, pipelineId, onImportComplete }) => {
  const fileInputRef = useRef(null);
  const [step, setStep] = useState("upload"); // upload, preview, importing, complete
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [loading, setLoading] = useState(false);

  const resetState = () => {
    setStep("upload");
    setFile(null);
    setPreviewData(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/import/template`);
      const csv = res.data.sample_csv;
      
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "opportunities_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith(".csv")) {
      toast.error("Please select a CSV file");
      return;
    }

    setFile(selectedFile);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      if (pipelineId) {
        formData.append("pipeline_id", pipelineId);
      }

      const res = await axios.post(`${API}/import/opportunities/preview`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setPreviewData(res.data);
      setStep("preview");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview CSV");
      resetState();
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setLoading(true);
    setStep("importing");

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (pipelineId) {
        formData.append("pipeline_id", pipelineId);
      }
      formData.append("skip_duplicates", skipDuplicates.toString());

      const res = await axios.post(`${API}/import/opportunities`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setImportResult(res.data);
      setStep("complete");
      
      if (res.data.imported > 0) {
        toast.success(`Imported ${res.data.imported} opportunities`);
        onImportComplete?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
      setStep("preview");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Import Opportunities from CSV
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file to bulk import opportunities into your pipeline.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {/* Step 1: Upload */}
          {step === "upload" && (
            <div className="space-y-6 py-4">
              {/* Template Download */}
              <div className="flex items-center justify-between p-4 border border-border rounded-lg bg-muted/20">
                <div>
                  <h3 className="font-medium">Download Template</h3>
                  <p className="text-sm text-muted-foreground">
                    Get a sample CSV with the correct column format
                  </p>
                </div>
                <Button variant="outline" onClick={downloadTemplate}>
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
              </div>

              {/* File Upload */}
              <div 
                className="border-2 border-dashed border-border rounded-lg p-12 text-center hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                {loading ? (
                  <>
                    <Loader2 className="h-12 w-12 mx-auto mb-4 text-primary animate-spin" />
                    <p className="text-muted-foreground">Processing file...</p>
                  </>
                ) : (
                  <>
                    <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="font-medium">Click to upload CSV</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      or drag and drop your file here
                    </p>
                  </>
                )}
              </div>

              {/* Column Info */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Required Columns</h3>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="bg-primary/10 text-primary">name *</Badge>
                  <Badge variant="outline">description</Badge>
                  <Badge variant="outline">estimated_value</Badge>
                  <Badge variant="outline">client_name</Badge>
                  <Badge variant="outline">city</Badge>
                  <Badge variant="outline">state</Badge>
                  <Badge variant="outline">due_date</Badge>
                  <Badge variant="outline">stage</Badge>
                  <Badge variant="outline">source</Badge>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === "preview" && previewData && (
            <div className="space-y-4 py-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 border border-border rounded-lg text-center">
                  <p className="text-2xl font-bold">{previewData.total_rows}</p>
                  <p className="text-sm text-muted-foreground">Total Rows</p>
                </div>
                <div className="p-4 border border-green-500/30 bg-green-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-400">{previewData.valid_rows}</p>
                  <p className="text-sm text-muted-foreground">Valid</p>
                </div>
                <div className="p-4 border border-amber-500/30 bg-amber-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-400">{previewData.rows_with_issues}</p>
                  <p className="text-sm text-muted-foreground">With Issues</p>
                </div>
              </div>

              {/* Options */}
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <Label htmlFor="skip-duplicates" className="font-medium">Skip Duplicates</Label>
                  <p className="text-sm text-muted-foreground">
                    Skip opportunities that already exist (by name)
                  </p>
                </div>
                <Switch
                  id="skip-duplicates"
                  checked={skipDuplicates}
                  onCheckedChange={setSkipDuplicates}
                />
              </div>

              {/* Preview Table */}
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="max-h-[300px] overflow-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-muted">
                      <TableRow>
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Value</TableHead>
                        <TableHead>Stage</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.preview.map((row) => (
                        <TableRow key={row.row_number}>
                          <TableCell className="text-muted-foreground">{row.row_number}</TableCell>
                          <TableCell className="font-medium">{row.parsed.name || "-"}</TableCell>
                          <TableCell>{row.parsed.client_name || "-"}</TableCell>
                          <TableCell>
                            {row.parsed.estimated_value 
                              ? `$${row.parsed.estimated_value.toLocaleString()}` 
                              : "-"
                            }
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {row.parsed.stage}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {row.issues.length === 0 ? (
                              <CheckCircle2 className="h-4 w-4 text-green-400" />
                            ) : (
                              <div className="flex items-center gap-1">
                                <AlertCircle className="h-4 w-4 text-amber-400" />
                                <span className="text-xs text-amber-400">{row.issues.length}</span>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Warnings */}
              {previewData.warnings.length > 0 && (
                <div className="p-3 border border-amber-500/30 bg-amber-500/10 rounded-lg">
                  <p className="text-sm text-amber-400 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    {previewData.warnings[0].message}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Importing */}
          {step === "importing" && (
            <div className="py-12 text-center">
              <Loader2 className="h-12 w-12 mx-auto mb-4 text-primary animate-spin" />
              <p className="text-lg font-medium">Importing opportunities...</p>
              <p className="text-muted-foreground">This may take a moment</p>
            </div>
          )}

          {/* Step 4: Complete */}
          {step === "complete" && importResult && (
            <div className="space-y-6 py-4">
              {/* Result Summary */}
              <div className="text-center py-6">
                {importResult.imported > 0 ? (
                  <>
                    <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-green-400" />
                    <h3 className="text-xl font-bold">Import Complete!</h3>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-16 w-16 mx-auto mb-4 text-amber-400" />
                    <h3 className="text-xl font-bold">No Opportunities Imported</h3>
                  </>
                )}
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div className="p-4 border border-border rounded-lg text-center">
                  <p className="text-2xl font-bold">{importResult.total_rows}</p>
                  <p className="text-sm text-muted-foreground">Total Rows</p>
                </div>
                <div className="p-4 border border-green-500/30 bg-green-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-400">{importResult.imported}</p>
                  <p className="text-sm text-muted-foreground">Imported</p>
                </div>
                <div className="p-4 border border-blue-500/30 bg-blue-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-400">{importResult.skipped}</p>
                  <p className="text-sm text-muted-foreground">Skipped</p>
                </div>
                <div className="p-4 border border-red-500/30 bg-red-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-400">{importResult.errors.length}</p>
                  <p className="text-sm text-muted-foreground">Errors</p>
                </div>
              </div>

              {/* Errors */}
              {importResult.errors.length > 0 && (
                <div className="border border-red-500/30 rounded-lg overflow-hidden">
                  <div className="p-3 bg-red-500/10 border-b border-red-500/30">
                    <p className="font-medium text-red-400">Errors</p>
                  </div>
                  <div className="max-h-[150px] overflow-auto">
                    {importResult.errors.map((err, i) => (
                      <div key={i} className="p-2 border-b border-border last:border-0 text-sm">
                        <span className="text-muted-foreground">Row {err.row}:</span>{" "}
                        <span className="text-red-400">{err.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          {step === "upload" && (
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
          )}
          
          {step === "preview" && (
            <>
              <Button variant="outline" onClick={resetState}>Back</Button>
              <Button onClick={handleImport} disabled={previewData?.valid_rows === 0}>
                Import {previewData?.valid_rows} Opportunities
              </Button>
            </>
          )}
          
          {step === "complete" && (
            <>
              <Button variant="outline" onClick={resetState}>Import More</Button>
              <Button onClick={handleClose}>Done</Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CSVImportDialog;
